

import { DailyMessage } from "../components/child/DailyMessage";

const PARCHMENT = { background: "linear-gradient(135deg, #f5e6d3 0%, #e8d4b8 50%, #f0e0c8 100%)" };

function DoneSeal() {
  return (
    <div style={{
      position: "absolute", top: 8, right: 8,
      background: "linear-gradient(135deg, #22c55e, #15803d)",
      color: "white", borderRadius: "50%", width: 28, height: 28,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)", zIndex: 10,
    }}>
      <svg style={{ width: 14, height: 14 }} fill="white" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
    </div>
  );
}

const BOOKS = [
  { key: "spelling",  label: "SPELLING",  bg: "linear-gradient(to bottom, #991b1b, #450a0a)", border: "#dc2626", textColor: "rgba(253,230,138,0.5)" },
  { key: "meaning",   label: "MEANINGS",  bg: "linear-gradient(to bottom, #1e3a8a, #0f172a)", border: "#3b82f6", textColor: "#fde68a" },
  { key: "synonym",   label: "SYNONYMS",  bg: "linear-gradient(to bottom, #064e3b, #022c22)", border: "#10b981", textColor: "rgba(253,230,138,0.5)" },
  { key: "antonym",   label: "ANTONYMS",  bg: "linear-gradient(to bottom, #4c1d95, #1e1b4b)", border: "#8b5cf6", textColor: "#fde68a" },
];

function WeeklyChart({ sessions }) {
  const today = new Date();
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Today"];
  const counts = days.map((_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    return sessions.filter(s => new Date(s.date).toDateString() === d.toDateString()).length;
  });
  const max = Math.max(...counts, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", height: 96, gap: 4 }}>
      {days.map((d, i) => {
        const h = Math.max(16, Math.round((counts[i] / max) * 90));
        const isToday = i === 6;
        return (
          <div key={d} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{
              width: "100%", borderRadius: "3px 3px 0 0", height: h,
              background: counts[i] > 0 ? (isToday ? "#f59e0b" : "#9b2226") : "#292524",
              border: counts[i] === 0 ? "1px dashed #57534e" : "none",
              outline: isToday ? "2px solid #f59e0b" : "none", outlineOffset: 1,
            }} />
            <span style={{
              fontFamily: "'Crimson Text', serif", fontSize: 11,
              color: isToday ? "#fbbf24" : "#a8a29e",
              fontWeight: isToday ? 700 : 400,
            }}>{d}</span>
          </div>
        );
      })}
    </div>
  );
}

export function WizardHomeLayout({
  user, data,
  onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest,
  settings,
}) {
  const mySessions = data.sessions.filter(s => s.userId === user.id);
  const today = new Date().toDateString();
  const doneMap = {
    spelling: mySessions.some(s => new Date(s.date).toDateString() === today && s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym"),
    meaning:  mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "meaning"),
    synonym:  mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "synonym"),
    antonym:  mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "antonym"),
  };
  const onClickMap = { spelling: onStartTest, meaning: onStartMeaningTest, synonym: onStartSynonymTest, antonym: onStartAntonymTest };

  const streak = data.streaks?.[user.id] || 0;
  const myPoints = data.points?.[user.id] || 0;
  const myGoalId = data.goals?.[user.id] || null;
  const myGoal = myGoalId ? (data.rewards || []).find(r => r.id === myGoalId) : null;
  const myProgress = Object.entries(data.progress || {}).filter(([k]) => k.startsWith(user.id) && !k.includes("_meaning_"));
  const mastered = myProgress.filter(([, v]) => v.mastered).length;
  const level = Math.floor(myPoints / 100);

  const allDone = Object.values(doneMap).every(Boolean);

  return (
    <div style={{ minHeight: "100vh", background: "#1c1917", fontFamily: "'Crimson Text', serif", paddingBottom: 32 }}>

      {/* Parchment header */}
      <header style={{ ...PARCHMENT, borderBottom: "4px solid #92400e", padding: "16px 24px" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 48, height: 48, background: "#92400e", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 24 }}>⭐</span>
            </div>
            <div>
              <h1 style={{ fontFamily: "'Cinzel Decorative', serif", color: "#92400e", fontSize: 18 }}>
                {settings?.appName || "SpellWise"}
              </h1>
              <p style={{ color: "#a16207", fontSize: 13, fontStyle: "italic" }}>Academy of Wordcraft</p>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontFamily: "'Cinzel Decorative', serif", color: "#92400e", fontSize: 13 }}>Level {level}</p>
            <p style={{ color: "#a16207", fontSize: 12 }}>⭐ {myPoints} pts</p>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 480, margin: "0 auto", padding: "24px 16px" }}>

        {/* Daily message — styled as a magical proclamation */}
        <DailyMessage userName={user.name} streak={streak} bgGradient="linear-gradient(135deg, #78350f 0%, #7c3aed 100%)" />

        {/* Welcome scroll */}
        <div style={{ ...PARCHMENT, borderRadius: 8, padding: 24, marginBottom: 24, border: "2px solid #92400e", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 8, right: 8, width: 32, height: 32, background: "#7f1d1d", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 8px rgba(139,0,0,0.4)" }}>
            <span style={{ fontFamily: "'Cinzel Decorative', serif", color: "#fde68a", fontSize: 12 }}>S</span>
          </div>
          <h2 style={{ fontFamily: "'Cinzel Decorative', serif", color: "#92400e", fontSize: 16, marginBottom: 4 }}>
            Welcome back, {user.name}
          </h2>
          <p style={{ color: "#a16207", fontStyle: "italic" }}>Your magical journey continues...</p>
          <div style={{ display: "flex", gap: 24, marginTop: 16 }}>
            {[
              { icon: "⭐", val: `Lv ${level}`, sub: "Apprentice" },
              { icon: "🪄", val: mastered, sub: "Spells" },
              { icon: "🔥", val: streak, sub: `Day${streak !== 1 ? "s" : ""} Streak` },
            ].map(({ icon, val, sub }) => (
              <div key={sub} style={{ textAlign: "center" }}>
                <div style={{ width: 40, height: 40, background: "#92400e", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 4px", fontSize: 18 }}>{icon}</div>
                <p style={{ color: "#92400e", fontSize: 13, fontWeight: 600 }}>{val}</p>
                <p style={{ color: "#a16207", fontSize: 11 }}>{sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Spellbooks */}
        <h3 style={{ fontFamily: "'Cinzel Decorative', serif", color: "#fde68a", fontSize: 16, marginBottom: 16, padding: "0 8px" }}>Choose Your Spellbook</h3>
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, marginBottom: 32 }}>
          {BOOKS.map(book => {
            const done = doneMap[book.key];
            const onClick = onClickMap[book.key];
            return (
              <div
                key={book.key}
                onClick={onClick}
                style={{ flexShrink: 0, width: 96, cursor: "pointer" }}
              >
                <div style={{
                  height: 144, background: book.bg, borderRadius: "6px 6px 6px 2px",
                  borderLeft: `4px solid ${book.border}`, boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  position: "relative", overflow: "hidden",
                  opacity: done ? 0.65 : 1,
                  transform: done ? "none" : "scale(1)",
                  transition: "transform .2s",
                }}>
                  {done && <DoneSeal />}
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, transparent, rgba(255,255,255,0.05), transparent)" }} />
                  {/* vertical spine text */}
                  <div style={{
                    position: "absolute", bottom: 12, left: "50%",
                    transform: "translateX(-50%) rotate(90deg)",
                    fontFamily: "'Cinzel Decorative', serif", fontSize: 9,
                    color: done ? "rgba(253,230,138,0.4)" : "#fde68a",
                    whiteSpace: "nowrap", letterSpacing: "0.1em",
                  }}>{book.label}</div>
                </div>
                {done
                  ? <p style={{ textAlign: "center", color: "#4ade80", fontFamily: "'Cinzel Decorative', serif", fontSize: 11, marginTop: 6 }}>Done!</p>
                  : <p style={{ textAlign: "center", color: "#60a5fa", fontFamily: "'Cinzel Decorative', serif", fontSize: 10, marginTop: 6 }}>Cast spell</p>
                }
              </div>
            );
          })}
        </div>

        {/* Daily tasks */}
        <div style={{ ...PARCHMENT, borderRadius: 8, padding: 20, marginBottom: 24, border: "2px solid #92400e" }}>
          <h3 style={{ fontFamily: "'Cinzel Decorative', serif", color: "#92400e", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            ✨ Daily Magical Tasks
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Complete a spelling quiz",  done: doneMap.spelling },
              { label: "Complete a meanings quiz",  done: doneMap.meaning },
              { label: "Complete synonyms & antonyms", done: doneMap.synonym && doneMap.antonym },
              { label: "Complete all spellbooks", done: allDone },
            ].map(({ label, done: taskDone }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(255,251,235,0.5)", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: taskDone ? "#166534" : "#d6d3d1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {taskDone
                    ? <svg style={{ width: 16, height: 16 }} fill="white" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg style={{ width: 14, height: 14 }} fill="#78716c" viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"/></svg>
                  }
                </div>
                <p style={{ color: "#92400e", fontSize: 14, flex: 1 }}>{label}</p>
                <span style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: 12, color: taskDone ? "#166534" : "#a16207" }}>{taskDone ? "Done!" : "Pending"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Goal banner */}
        {myGoal && (
          <div style={{ ...PARCHMENT, borderRadius: 8, padding: 16, marginBottom: 24, border: "2px solid #92400e" }}>
            <h3 style={{ fontFamily: "'Cinzel Decorative', serif", color: "#92400e", marginBottom: 8 }}>🎯 Quest Goal</h3>
            <p style={{ color: "#374151", fontSize: 14, marginBottom: 8 }}>{myGoal.emoji} {myGoal.name}</p>
            <div style={{ background: "#fde68a", borderRadius: 999, height: 12, overflow: "hidden" }}>
              <div style={{ height: "100%", background: "#15803d", borderRadius: 999, width: `${Math.min(100, Math.round((myPoints / myGoal.cost) * 100))}%`, transition: "width .5s" }} />
            </div>
            <p style={{ fontFamily: "'Cinzel Decorative', serif", color: "#92400e", fontSize: 12, marginTop: 6 }}>⭐ {myPoints} / {myGoal.cost} pts</p>
          </div>
        )}

        {/* Weekly chart */}
        <div style={{ background: "#292524", borderRadius: 8, padding: 20, border: "1px solid #a16207" }}>
          <h3 style={{ fontFamily: "'Cinzel Decorative', serif", color: "#fde68a", marginBottom: 16, fontSize: 14 }}>House Points This Week</h3>
          <WeeklyChart sessions={mySessions} />
        </div>

      </main>
    </div>
  );
}
